import { Inject, inject, Injectable } from '@angular/core';
import { ISearchParams, ISmCategory, IRoomOccupancy, IAlternativeSearchParams, IAlternativeRoomSummary, IAlternativeOtherHotelDto, IAlternativeOtherHotelsSearchParams } from '../interfaces';
import { Router } from '@angular/router';
import { StorageService } from './storage.service';
import { HttpClient, provideHttpClient } from '@angular/common/http';
import { concatMap, forkJoin, from, map, Observable, of } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { formatDate } from "@angular/common";
import { ShopcartService } from './shopcart.service';
import { PIMService } from './pim.service';
import dayjs from 'dayjs';
import { SettingsService } from './settings.service';
import { LanguageService } from './language.service';
import { v4 as uuidv4 } from 'uuid';
import { AnalyticsService } from './analytics.service';
import { DebugService } from './debug.service';
import { IAvailabilityData } from  '../interfaces';


@Injectable({
  providedIn: 'root'
})
export class AvailabilityService {

  availability$ = new BehaviorSubject<null | any[]>(null);
  // expose the BehaviorSubject as an Observable
  availability = this.availability$.asObservable();

  allotments$ = new BehaviorSubject<null | any[]>(null);
  // expose the BehaviorSubject as an Observable
  allotments = this.allotments$.asObservable();

  private environment: {[key: string]: string};

  constructor(
    private router: Router, 
    private storage: StorageService, 
    private http: HttpClient,
    private shopCartService: ShopcartService,
    private PIM: PIMService,
    private settings: SettingsService,
    private languageService: LanguageService,
    private analyticsService: AnalyticsService,
    private debug: DebugService,
    
    @Inject('environment')
    environment: {[key: string]: string}

  ) {
    this.environment = environment;
  }

  getDatePickerAvailability(hotelCode:string, arrival:string, departure:string) {
    //dateformat YYYY-MM-DD

    let endpoint = this.settings.getEndpoint(hotelCode);
    if (!endpoint) {
      return of(false);
    }



    let url = `${endpoint}/Availability/getcalendar?ArrivalDate=${arrival}&DepartureDate=${departure}&HotelCode=${hotelCode}`;
    return this.http.get(url, {
      responseType: 'json',
      headers: {
        'X-Conversation-ID': uuidv4()
      }
    }).pipe(map((res:any) => {
      // order the data to suit the calendar better
      let orderedDays:any = [];
      let searchmaskCategories = this.settings.get('searchMask.categories');

      if (res.success && res.data.length > 0) {
        let days = res.data[0].days;
        days.forEach((d:any) => {
          let totalRooms = 0;
          let perRoomCode:any = {};
          let perCategory:any = {};
          
          if (searchmaskCategories && searchmaskCategories.length > 0) {
           
            searchmaskCategories.forEach((category:any) => {
              if (category.value != 'ALL') {
                perCategory[category.value] = 0;
              }
            });
          }
          res.data.forEach((roomType:any) => {
            let dayAvailabilityForRoomType = roomType.days.find((item:any) => {
              return item.date == d.date
            });
            if (dayAvailabilityForRoomType) {
              
              totalRooms += dayAvailabilityForRoomType.availableRooms;
              perRoomCode[roomType.roomCode] = dayAvailabilityForRoomType.availableRooms;
              if (searchmaskCategories && searchmaskCategories.length > 0) {
                searchmaskCategories.forEach((category:any) => {
                  if (category.roomCodes && category.roomCodes.includes(roomType.roomCode)) {
                    perCategory[category.value] += dayAvailabilityForRoomType.availableRooms;
                  }
                });
              }
            }
          });
          let onlyDate = d.date.split("T")[0]; //remove T00:00:00 after the date
  
          orderedDays.push({
            date: onlyDate,
            totalAvailability: totalRooms,
            perRoomCode: perRoomCode,
            perCategory: perCategory
          });
        });
      }
      
      return orderedDays;
    }));
  }

  getCategoryName(roomObject:any) {
    let categories = this.settings.getCategories(this.languageService.getCurrentLanguage());
    if (this.settings.get('searchMask.usePmsCategories')) {
      if (categories && categories.length > 0) {
        let cat = categories.find((item:ISmCategory) => item.value == roomObject.roomCategory);
        if (cat) {
          return cat.name
        }
      } 
    }
    else {
      return roomObject.beRoomCategoryName;
    }    
  }

  // called if deeplink does not have makeSearch param
  setSearchParamsAndNavigateHome(searchParams: ISearchParams) {
    this.storage.saveSessionStorage('searchparams', searchParams);
    //clear the shopcart
    this.shopCartService.clearShopCart();
    this.router.navigate(['home']);
  }

  makeSearch(searchParams: ISearchParams) {
    //searchParams are saved to session storage

    this.storage.saveSessionStorage('searchparams', searchParams);

    //clear the shopcart
    this.shopCartService.clearShopCart();

    // set phase to availability

    if (!searchParams.hotelCode || searchParams.hotelCode.length == 0 || searchParams.hotelCode == 'ALL') {
      this.router.navigate(['hotels']);
    }
    else {
      this.router.navigate(['availability']);
    }
    try {
      this.analyticsService.bookingEngineMakeSearch(searchParams);
    } catch (error) {
      console.error('Error in analytics bookingEngineMakeSearch: ', error);
    }
  }

  // called from customer searchmask
  makeDeeplinkSearch(searchParams: ISearchParams) {
    //searchParams are saved to session storage
    this.storage.saveSessionStorage('searchparams', searchParams);
    let baseUrl = this.settings.get('app.bookingEngineUrl');

    if (this.environment.isDev) {
      baseUrl = "http://localhost:4200"
    }

    document.location = this.buildDeeplinkUrl(baseUrl, searchParams);
  }

  // builds the deeplink URL for a given base URL and search params.
  // extracted from makeDeeplinkSearch so it can be reused (e.g. alternative-availability links)
  // without session storage / document.location side effects.
  buildDeeplinkUrl(baseUrl: string, searchParams: ISearchParams, excludeBookingCode?: boolean): string {
    let language = this.languageService.getCurrentLanguage();

    let arrival = dayjs(searchParams.arrival).format('DD-MM-YYYY');
    let departure = dayjs(searchParams.departure).format('DD-MM-YYYY');

    let roomDataString = '';
    searchParams.roomData.forEach((room:IRoomOccupancy, index: number) => {
      roomDataString+=`&room${index}=${room.adults}|${room.children}|${room.childAges.join('-')}`;
    });

    let bookingCode = excludeBookingCode ? '' : (searchParams.bookingCode ?? '');

    let deeplink = `${baseUrl}/deeplink/`+
      `?lang=${language}`+
      `&makeSearch=1`+
      `&hotelCode=${searchParams.hotelCode}`+
      `&chainCode=${searchParams.chainCode}`+
      `&arrival=${arrival}`+
      `&departure=${departure}`+
      `&category=${searchParams.category}`+
      `&bookingCode=${bookingCode}`+
      `${roomDataString}`;

    return deeplink;
  }

  selectHotelAndMakeSearch(hotelCode:string) {
    //triggered from hotels page
    try {
      let sp = this.getSearchParams();
      if (sp) {
        sp.hotelCode = hotelCode;
        this.makeSearch(sp);
      }  
    }
    catch(e) {
      console.error('Error in selectHotel: ',e);
    }
     
  }

  selectRoomRate(roomRate:any, roomIndex:number) {
    let searchParams:ISearchParams|null = this.getSearchParams();
    if (searchParams) {
      if (this.getSearchParams()?.roomData?.length == 1) {
        // single room
        this.shopCartService.addCartItem(roomRate, searchParams);
        // set phase to profile
        this.router.navigate(['profile']);
      }
      else {
        // multiroom
  
        let cart = this.shopCartService.getCart();
        if (cart && cart.getCartItemsByType('roomrate').length >= roomIndex+1) {
         
          this.shopCartService.replaceCartItem(roomIndex, roomRate, searchParams);
          
        }
        else{
          this.shopCartService.addCartItem(roomRate, searchParams);
        }
      }
      try {
        this.analyticsService.bookingEngineAddToCart(roomRate, searchParams);
      } catch (error) {
        console.error('Error in analytics bookingEngineAddToCart: ', error);
      }
    }

    
  }

  multiRoomContinue() {
    this.router.navigate(['profile']);
  }

  getSearchParams():ISearchParams|null {
    let sp = this.storage.loadSessionStorage('searchparams');

    if ( sp) {
      sp.arrival = this.toLocalIsoDate(sp.arrival);
      sp.departure = this.toLocalIsoDate(sp.departure);
    }

    return sp;
  }

  getSearchDaysDelta():number {
    let sm = this.getSearchParams();
    if (sm) {
       return dayjs(sm.departure).diff(sm.arrival, 'day');
    }
    return 0;
  }

  createSearchUrl(roomIndex:number) {
    let search = this.getSearchParams();
    let arrival, departure, rooms, adults, children, childAges, endpoint;

    let language = this.languageService.getCurrentLanguage().toUpperCase();


    if (typeof search?.arrival == 'string') {
      arrival = this.toApiDate(search?.arrival);
    }
    if (typeof search?.departure == 'string') {
      departure = this.toApiDate(search?.departure);
    }
    if (search?.roomData) {
      rooms = 1;
      adults = search?.roomData[roomIndex].adults;
      children = search?.roomData[roomIndex].children;
      childAges = search?.roomData[roomIndex].childAges?.join("|");
    }
    let bookingCode = search?.bookingCode ?? '';

    if (search?.hotelCode) {
      endpoint = this.settings.getEndpoint(search.hotelCode);
    }

    
    return `${endpoint}/Availability/hotelroomrates?ArrivalDate=${arrival}&DepartureDate=${departure}&HotelCode=${search?.hotelCode}&Rooms=${rooms}&NroOfAdults=${adults}&NroOfChildren=${children}&ChildAges=${childAges}&BookingCode=${bookingCode}&LanguageCode=${language}`;
  }

  getAllotments() {
    let search:ISearchParams|null = this.getSearchParams();
    if (!search) {
      return;
    }

    // backendin HotelId on sama kuin hakuparametrien hotelCode (numeerisena)
    let hotelId = Number(search.hotelCode);
    if (!Number.isInteger(hotelId) || hotelId <= 0) {
      console.error('could not load allotments: invalid hotelCode', search.hotelCode);
      return;
    }

    let endpoint = this.settings.getEndpoint(search.hotelCode);
    let url = `${endpoint}/Availability/allotments/find`;
    let body = {
      "hotelId": hotelId,
      "startDate": this.toLocalIsoDate(search.arrival),
      "endDate": this.toLocalIsoDate(search.departure),
    }

    this.http.post(url, body, {
      responseType: 'json',
      headers: {
        'X-Conversation-ID': uuidv4()
      }
    })
    .subscribe((res:any) => {
      if (res.success) {
        this.allotments$.next(res.data);
      }
      else {
        console.error('could not load allotments');
      }
    });
  }

  fetchAvailability():any {

    let searchParams = this.getSearchParams();
    //if eg 3 rooms searched, make search 3 times
    if (searchParams) {
      let roomCount = searchParams.roomData?.length || 0;

      // if there is a booking code, get allotment list otherwise set it to empty
      if (searchParams.bookingCode) {
        this.getAllotments();
      }
      else {
        this.allotments$.next(null);
      }

      let conversationId = uuidv4();
      
      // create get request per searchmask
      let searches = [];
      for (let i=0; i<roomCount; i++) {
        let url = this.createSearchUrl(i);
        searches.push(this.http.get(url, {
          responseType: 'json',
          headers: {
            'X-Conversation-ID': conversationId
          }
        }))
      }

      // run the requests parallel and return when all are resolved
      forkJoin([...searches]).subscribe({
          error: (e) => {
            console.error('ERROR IN SEARCH');
            this.availability$.next([]);
          },
          next: (result) => { 

            let sameDayMaxHour = this.settings.get('availability.sameDayReservationsMaxHour');
            let sameDayMaxDays = this.settings.get('availability.sameDayReservationsMaxDays');
            if ((sameDayMaxHour || sameDayMaxDays) && searchParams.arrival) {
              this.debug.log('Checking same day reservation limits', sameDayMaxHour);

              let arrival = dayjs(searchParams.arrival);
              let maxDay = dayjs(); //today
              // if max days set, set today 
              if (sameDayMaxDays) {
                maxDay = maxDay.add(sameDayMaxDays, 'day');
              }

              if (arrival.isBefore(maxDay, 'day') || (arrival.isSame(maxDay, 'day') && maxDay.hour() >= sameDayMaxHour)) {
                // if time is after the max hour, remove all rooms from the result
                this.debug.log('SAME DAY limit exceeded, removing all rooms from availability');
                this.availability$.next(['SAMEDAY_LIMIT_EXCEEDED']);
                return
              }
            }

            let bookingCutoffDays = this.settings.get('availability.bookingCutoffDays');
            if (bookingCutoffDays) {
              bookingCutoffDays = parseInt(bookingCutoffDays);

              
              let arrival = dayjs(searchParams.arrival);
              let maxDay = dayjs().add(bookingCutoffDays, 'day');

              this.debug.log('Checking booking cutoff limits', bookingCutoffDays, maxDay.format('YYYY-MM-DD'), arrival.format('YYYY-MM-DD'));

              // if max day set 
              if (arrival.isAfter(maxDay, 'day')) {
                this.debug.log('BOOKING CUTOFF limit exceeded, removing all rooms from availability');
                this.availability$.next(['BOOKING_CUTOFF_LIMIT_EXCEEDED']);
                return
              }
            }

            let modifiedAvailability:IAvailabilityData[] = [];
      
            result.forEach((roomResult:any) => { 

              let rooms:any = [];
      
              let rc:string[] = [];
              let berc: string[] = [];
              // modify the result object
              roomResult.data.forEach((roomrate:any) => {

                if (!rc.includes(roomrate.room.roomCategory)) {
                  rc.push(roomrate.room.roomCategory)
                }
      
                let roomCodeFoundIndex = rooms.findIndex((item:any) => item.roomCode == roomrate.room.roomCode);
      
                let beRoomCategory =  this.PIM.getBeRoomCategory(roomrate.room.roomCode);
                roomrate.room.beRoomCategoryName = beRoomCategory?.name;
                roomrate.room.beRoomCategoryCode = beRoomCategory?.value;

                if (!berc.includes(roomrate.room.beRoomCategoryCode)) {
                  berc.push(roomrate.room.beRoomCategoryCode)
                }
                roomrate.room.pim = this.PIM.getRoomData(searchParams.hotelCode, roomrate.room.roomCode);
                roomrate.rate.pim = this.PIM.getRateData(searchParams.hotelCode, roomrate.rate.rateCode, roomrate.reservationAnnotations);
                roomrate.rate.isLastMinute = roomrate.reservationAnnotations?.includes('LAST_MINUTE');
                roomrate.rate.isNonRef = roomrate.reservationAnnotations?.includes('NON_REFUNDABLE');
      
                if (roomCodeFoundIndex == -1) {
                  rooms.push({
                    roomCode: roomrate.room.roomCode,
                    realTimeAvailableRooms: roomrate.room.numberOfRoomsAvailable,
                    roomrates: [roomrate],
                  });
                }
                else {
                  rooms[roomCodeFoundIndex].roomrates.push(roomrate);
                }
              });

              this.debug.log('all PMS room categories', rc, 'all BE room categories', berc);

              rooms = this.sortRooms(rooms);

              if (this.settings.get('availability.hideOtherRoomsWhenCodeUsed') && searchParams.bookingCode) {
                //check if booking code is in allotments. 
                let codeFoundInAllotment = this.allotments$.getValue()?.find((allotment:any) => allotment.code.toLowerCase() == searchParams?.bookingCode.toLowerCase());
                if (!codeFoundInAllotment) {
                  // filter out rooms which have no booking code set
                  rooms = rooms.filter((room:any) => room.bookingCode != null);
                  // filter out rates which dont have the booking code.
                  rooms.forEach((room:any) => {
                    room.roomrates = room.roomrates.filter((r:any) => {
                      return r.rate.bookingCode?.toLowerCase() == searchParams?.bookingCode?.toLowerCase()
                    });
                  });
                }
                
              }
      

              modifiedAvailability.push(rooms);
            });
            this.availability$.next(modifiedAvailability);
            
            try {
              this.analyticsService.bookingEngineViewItemList(modifiedAvailability, searchParams);
            } catch (error) {
              console.error('Error in analytics bookingEngineViewItemList: ', error);
            }
            
        }
      });
    }
    
    
  }
  
  // parameterized, side-effect-free availability search: does not touch session storage,
  // availability$/allotments$, PIM enrichment, or booking-code allotment filtering.
  // Used by the alternative-availability feature to search other hotels/dates
  // without disturbing the primary search's shared state.
  searchAvailabilityFor(params: IAlternativeSearchParams): Observable<IAlternativeRoomSummary[][]> {
    let language = this.languageService.getCurrentLanguage().toUpperCase();
    let endpoint = this.settings.getEndpoint(params.hotelCode);
    let bookingCode = params.bookingCode ?? '';
    let conversationId = uuidv4();

    let searches = params.roomData.map((room: IRoomOccupancy) => {
      let rooms = 1;
      let adults = room.adults;
      let children = room.children;
      let childAges = room.childAges?.join("|");

      let url = `${endpoint}/Availability/hotelroomrates?ArrivalDate=${params.arrival}&DepartureDate=${params.departure}&HotelCode=${params.hotelCode}&Rooms=${rooms}&NroOfAdults=${adults}&NroOfChildren=${children}&ChildAges=${childAges}&BookingCode=${bookingCode}&LanguageCode=${language}`;

      return this.http.get(url, {
        responseType: 'json',
        headers: {
          'X-Conversation-ID': conversationId
        }
      });
    });

    return forkJoin([...searches]).pipe(map((result: any[]) => {
      return result.map((roomResult: any) => {
        let rooms: any = [];

        roomResult.data.forEach((roomrate: any) => {
          let roomCodeFoundIndex = rooms.findIndex((item: any) => item.roomCode == roomrate.room.roomCode);

          if (roomCodeFoundIndex == -1) {
            rooms.push({
              roomCode: roomrate.room.roomCode,
              realTimeAvailableRooms: roomrate.room.numberOfRoomsAvailable,
              roomrates: [roomrate],
            });
          }
          else {
            rooms[roomCodeFoundIndex].roomrates.push(roomrate);
          }
        });

        rooms = this.sortRooms(rooms);

        return rooms.map((room: any): IAlternativeRoomSummary => ({
          roomCode: room.roomCode,
          realTimeAvailableRooms: room.realTimeAvailableRooms,
          minPrice: room.minPrice
        }));
      });
    }));
  }

  // asks the backend for availability in the tenant's configured cross-tenant alternative hotels.
  // that configuration is backend-only, so the frontend cannot tell whether any exists: it always calls,
  // and a tenant without the setting simply gets an empty list. Searches the first room's occupancy
  // only, like the other alternative searches (which read rooms[0]).
  searchAlternativeOtherHotels(params: IAlternativeOtherHotelsSearchParams): Observable<IAlternativeOtherHotelDto[]> {
    const room = params.roomData[0];
    const childAges = room.childAges?.join('|') ?? '';
    const url = `${this.settings.getEndpoint()}/Availability/alternativeotherhotels?ArrivalDate=${params.arrival}&DepartureDate=${params.departure}&Rooms=1&NroOfAdults=${room.adults}&NroOfChildren=${room.children}&ChildAges=${childAges}`;

    return this.http.get<{ data: IAlternativeOtherHotelDto[] }>(url, {
      responseType: 'json',
      headers: { 'X-Conversation-ID': uuidv4() },
    }).pipe(map(response => response.data ?? []));
  }

  // summarizes a list of alternative room summaries into the lowest price and total available rooms.
  // used by the alternative-availability feature to render a compact per-hotel/date summary.
  summarizeAvailability(rooms: IAlternativeRoomSummary[]): { minPrice: number | null; roomsAvailable: number } {
    if (rooms.length === 0) {
      return { minPrice: null, roomsAvailable: 0 };
    }

    return {
      minPrice: Math.min(...rooms.map(r => r.minPrice)),
      roomsAvailable: rooms.reduce((sum, r) => sum + r.realTimeAvailableRooms, 0),
    };
  }

  //sort rooms by the applied sorting type,

  sortRooms(rooms: any[]): any[] {
    const sortBy = this.settings.get('availability.sorting') ?? 'DEFAULT'; // 'PRICE-ASC', 'PRICE-DESC', 'CLOUD', 'PIM'
    
    rooms.forEach((room: any) => {
      // Sort roomrates: bookingCode first, then by price
      room.roomrates.sort((a: any, b: any) => {
        const aHasCode = !!a.rate.bookingCode;
        const bHasCode = !!b.rate.bookingCode;

        if (aHasCode !== bHasCode) {
          return Number(bHasCode) - Number(aHasCode); // true > false
        }

        // Sort by price
        if (sortBy === 'PRICE-ASC' || sortBy === 'CLOUD' || sortBy === 'PIM') {
          return a.totalPriceForStayWithTaxes - b.totalPriceForStayWithTaxes;
        } else if (sortBy === 'PRICE-DESC') {
          return b.totalPriceForStayWithTaxes - a.totalPriceForStayWithTaxes;
        }

        return 0;
      });

      // Add bookingCode flag for room-level sort
      const bookingRate = room.roomrates.find((rr: any) => rr.rate.bookingCode);
      room.bookingCode = bookingRate?.rate.bookingCode || null;

      // Extract minimum price across roomrates
      room.minPrice = Math.min(...room.roomrates.map((rr: any) => rr.totalPriceForStayWithTaxes));
    });

    // Sort rooms
    rooms.sort((a: any, b: any) => {
      const aHasCode = !!a.bookingCode;
      const bHasCode = !!b.bookingCode;

      if (aHasCode !== bHasCode) {
        return Number(bHasCode) - Number(aHasCode);
      }

      if (sortBy === 'PRICE-ASC') {
        return a.minPrice - b.minPrice;
      } else if (sortBy === 'PRICE-DESC') {
        return b.minPrice - a.minPrice;
      } else if (sortBy === 'CLOUD') {
        // use room.sortOrder
        const aOrder = a.roomrates[0]?.room?.sortOrder ?? Number.MAX_SAFE_INTEGER;
        const bOrder = b.roomrates[0]?.room?.sortOrder ?? Number.MAX_SAFE_INTEGER;
        return aOrder - bOrder;
      } else if (sortBy === 'PIM') {
        // use room.pim.sortOrder
        const aOrder = a.pim?.sortOrder ?? Number.MAX_SAFE_INTEGER;
        const bOrder = b.pim?.sortOrder ?? Number.MAX_SAFE_INTEGER;
        return aOrder - bOrder;
      }

      return 0;
    });

    //override with deeplink params
    const deepLinkRoomPriorityOrder:string|undefined = this.storage.loadSessionStorage('roomPriorityOrder');
    const deeplinkRatePriorityOrder:string|undefined = this.storage.loadSessionStorage('ratePriorityOrder');
    
    // first sort rates by priority order
    if (deeplinkRatePriorityOrder) {
      let prioRates:string[] = deeplinkRatePriorityOrder.split(',');
      
      // Sort rates within each room
      rooms.forEach((room:any) => {
      room.roomrates.sort((a:any, b:any) => {
        const aIndex = prioRates.findIndex(p =>
          (a?.rate?.rateCode ?? '').startsWith(String(p).trim())
        );
        const bIndex = prioRates.findIndex(p =>
          (b?.rate?.rateCode ?? '').startsWith(String(p).trim())
        );
        if (aIndex === -1 && bIndex === -1) return 0; // both not in priority list
        if (aIndex === -1) return 1; // a not in priority, b is
        if (bIndex === -1) return -1; // b not in priority, a is
        return aIndex - bIndex; // both in priority, sort by index
      });
      });

      // Sort rooms - put rooms with priority rates first
      rooms.sort((a:any, b:any) => {
      const aHasPriorityRate = a.roomrates.some((rr: any) => {
        const code = rr?.rate?.rateCode ?? '';
        return prioRates.some((p: any) => code.startsWith(String(p)));
      });
      const bHasPriorityRate = b.roomrates.some((rr:any) => {
        const code = rr?.rate?.rateCode ?? '';
        return prioRates.some((p: any) => code.startsWith(String(p)));
      });

      if (aHasPriorityRate !== bHasPriorityRate) {
        return Number(bHasPriorityRate) - Number(aHasPriorityRate); // true > false
      }
      return 0; // maintain existing order if both have or don't have priority rates
      });
    }

    // if roompriority order present, if takes priority over rate sorting.
    if (deepLinkRoomPriorityOrder) {
      let prioRooms:string[] = deepLinkRoomPriorityOrder.split(',');
      rooms.sort((a:any, b:any) => {
        const aIndex = prioRooms.indexOf(a.roomCode);
        const bIndex = prioRooms.indexOf(b.roomCode);

        if (aIndex === -1 && bIndex === -1) return 0; // both not in priority list
        if (aIndex === -1) return 1; // a not in priority, b is
        if (bIndex === -1) return -1; // b not in priority, a is
        return aIndex - bIndex; // both in priority, sort by index
      });
    }

    return rooms;
  }

  /** Allotment code(s) for the current search: the booking code, if it matches a loaded allotment. */
  getSelectedAllotmentCodes(): string[] {
    const bookingCode = this.getSearchParams()?.bookingCode;
    if (!bookingCode) return [];
    const allotments = this.allotments$.getValue() ?? [];
    return allotments.some((a: any) => a?.code === bookingCode) ? [bookingCode] : [];
  }

  toApiDate(datestring:string):string {
    return formatDate(datestring, "yyyy-MM-dd", 'en-US');
  }

  toLocalIsoDate(datestring:string|Date):string {
    return formatDate(datestring, "yyyy-MM-ddTHH:mm:ss.SSS", 'en-US');
  }
}
